import { type CSSProperties } from 'react';

import { tailwindVariants } from '@air/utils-tailwind';

/**
 * Chrome shared by every picker row — `WorkspaceAgentAssetMentionRow` and `WorkspaceAgentBoardTreeRow`.
 * Hover and the highlight are the same gray fill, as in the canvas picker: the highlight sits where
 * the pointer would put it, so a colour of its own would read as a second kind of row.
 */
export const workspaceAgentMentionRow = tailwindVariants({
  base: 'flex min-h-8 w-full shrink-0 cursor-pointer items-center gap-2 rounded-8 p-1 text-left text-14 text-gray-12 outline-none hover:bg-gray-4 active:bg-gray-5',
  defaultVariants: {
    isSelected: false,
  },
  variants: {
    isDisabled: {
      true: 'cursor-not-allowed hover:bg-transparent active:bg-transparent',
    },
    isSelected: {
      // Hover restated so it doesn't beat the highlight on the row under the pointer.
      true: 'bg-gray-4 hover:bg-gray-4',
    },
  },
});

/** `gap-1` between mention rows. The virtual asset list passes the same inset to its virtualizer. */
export const WORKSPACE_AGENT_MENTION_ROW_GAP_PX = 4;

/** Drawn outside the row: the ring's own width, plus the gap it is held off the row's box by. */
const MENTION_ROW_RING_WIDTH_PX = 2;
const MENTION_ROW_RING_OFFSET_PX = 2;

/** What the ring needs clear of the row on every side, or the list's scroll container cuts it. */
export const WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX = MENTION_ROW_RING_WIDTH_PX + MENTION_ROW_RING_OFFSET_PX;

/**
 * A row the pointer highlighted, which is the fill and nothing else.
 *
 * Stated rather than left to the cascade: Tailwind's `outline-none` is a *transparent* 2px ring, so a
 * row that reads as ringless to the eye still measures as ringed. Specs tell the two highlights apart
 * via `data-mention-keyboard-highlight`, not computed outline.
 */
export const WORKSPACE_AGENT_MENTION_ROW_UNRINGED_STYLE: CSSProperties = { outlineStyle: 'none' };

/**
 * A row the *keyboard* moved the highlight to. The fill alone cannot say which input is driving the
 * list, and the editor — not the row — is what holds focus, so the row `aria-activedescendant` points
 * at carries the focus ring instead.
 *
 * Inline rather than `outline-2 outline-blue-9 outline-offset-2`: component specs run without the
 * app's stylesheet, so a class would compute to nothing at all. Longhand, not the `outline` shorthand,
 * so the offset survives a colour the harness cannot resolve. Specs assert `data-mention-keyboard-highlight`
 * and list clearance geometry, not this outline.
 */
export const WORKSPACE_AGENT_MENTION_ROW_KEYBOARD_RING_STYLE: CSSProperties = {
  // The token through its own variable; the literal behind it is that token's light value, for the harness.
  outlineColor: 'rgb(var(--colors-blue9-rgb, 3, 75, 255))',
  outlineOffset: `${MENTION_ROW_RING_OFFSET_PX}px`,
  outlineStyle: 'solid',
  outlineWidth: `${MENTION_ROW_RING_WIDTH_PX}px`,
};

/**
 * Ring clearance on every side: negative margin eats into the list inset, matching padding puts rows back
 * where they were while the scroll container keeps room the outline can paint into without clipping.
 * Inline for the same reason as the ring — the clearance is only worth having if it measures.
 */
export const WORKSPACE_AGENT_MENTION_LIST_RING_CLEARANCE_STYLE: CSSProperties = {
  marginBottom: -WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX,
  marginLeft: -WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX,
  marginRight: -WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX,
  marginTop: -WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX,
  paddingBottom: WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX,
  paddingLeft: WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX,
  paddingRight: WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX,
  paddingTop: WORKSPACE_AGENT_MENTION_ROW_RING_CLEARANCE_PX,
};
